import moment from 'moment';
import validateSsn from './../helpers/validateSsn';
import validateAge from './../helpers/validateAge';

// Swedish-collated comparator so the bookable services list shows alphabetically
// (å/ä/ö in Swedish order). New ClinicBuddy services default to activity_order 0 and
// used to jump to the top of their group; sorting the displayed list by name keeps every
// service in its alphabetical position without relying on activity_order being set per
// service. Verified against live prod data to reproduce NK's existing order exactly,
// moving only the unordered new service. (NK/Sofia, 2026-06.)
const SV_COLLATOR = new Intl.Collator('sv');
const byActivityName = (a, b) =>
  SV_COLLATOR.compare((a && a.activity_name) || '', (b && b.activity_name) || '');

/*
  NK 2026-08-25 (Sofia, NK): för tjänsten "Nordiska SkinMap AI" (activity_id 444) ska
  behandlarens NAMN inte visas för patienten. I stället visas "Hudläkare" på namnraden och
  titeln "Certified SkinMap Specialist" står kvar. Gäller ENDAST denna tjänst (uttryckligt
  önskemål) — samma behandlare visas med namn på alla andra tjänster.

  Matchar på activity_id, inte namn: aktiviteten döptes om i ClinicBuddy 2026-08-25
  ("Nordiska SkinMap AI" -> "Prickkontroll + Nordiska SkinMap AI"), id:t är stabilt.

  Regeln sitter i de två delade formatterarna nedan, så den slår igenom på alla ytor som
  visar behandlare: kalenderrutan (desktop + mobil), "Din bokning"-rutan, bokningsbekräftelsen
  och behandlarfiltret.
*/
const PROVIDER_NAME_HIDDEN_ACTIVITY_IDS = {
  444: { name: 'Hudläkare', title: 'Certified SkinMap Specialist' },
};

function providerNameOverrideFor(state) {
  var selected = (state && state.activities && state.activities.selected) || null;
  var id = selected && selected.activity_id != null ? String(selected.activity_id) : '';
  return Object.prototype.hasOwnProperty.call(PROVIDER_NAME_HIDDEN_ACTIVITY_IDS, id)
    ? PROVIDER_NAME_HIDDEN_ACTIVITY_IDS[id]
    : null;
}

export default {
  dateIsInThePast(state) {
    return (
      state.dates.current.weekStartDate >= state.dates.active.weekStartDate
    );
  },
  activityIsCampaign: (state) => (activity) => {
    var name = activity.activity_name.toLowerCase();
    if (name.indexOf('kampanj') !== -1) {
      return true;
    }
    return false;
  },
  formatProviderName: (state) => (name) => {
    /* Se PROVIDER_NAME_HIDDEN_ACTIVITY_IDS ovan: dölj behandlarnamnet för vissa tjänster. */
    var nameOverride = providerNameOverrideFor(state);
    if (nameOverride) {
      return (
        '<span class="practitioner-name">' + nameOverride.name + '</span>' +
        '<span class="practitioner-title">' + nameOverride.title + '</span>'
      );
    }
    /*
		var index = name.indexOf('Leg. Läkare');
		name = name.substring(0, index != -1 ? index : name.length);
		*/
    var newName = name.replace(
      'Sandra Sellström Leg. Sjuksköterska',
      'Sandra Sellström Injektionssjuksköterska'
    );
    newName = newName.replace(
      'Silvia Galaz Leg. Sjuksköterska',
      'Silvia Galaz Injektionssjuksköterska'
    );
    newName = newName.replace(
      'Anna Hamberg Operationssjuksköterska',
      'Anna Hamberg Specialistsjuksköterska'
    );
    newName = newName.replace(
      'Specialistsjuksköterska',
      '<br />Specialistsjuksköterska'
    );
    newName = newName.replace(
      'Leg. Läkare. Specialist i plastikkirurgi ',
      '<br />Plastikkirurg'
    );
    newName = newName.replace(
      'Leg. Läkare. Specialist i plastikkirurgi',
      '<br />Plastikkirurg'
    );
    newName = newName.replace(
      'Leg. Läkare Specialist i plastikkirurgi',
      '<br />Plastikkirurg'
    );
    newName = newName.replace(
      'Injektionsspecialist',
      '<br />Injektionsspecialist'
    );
    // Carolina Ryberg: NK wants her shown as "Certified SkinMap Specialist" in the booking
    // (they declined to change her name in ClinicBuddy). Must run BEFORE the generic
    // "Aukt. Hudterapeut" -> "Hudterapeut" rule so her custom title wins; everyone else is
    // untouched. Same per-provider override pattern as Sandra Sellström / Silvia Galaz below.
    newName = newName.replace('Carolina Ryberg Aukt. Hudterapeut', 'Carolina Ryberg<br />Certified SkinMap Specialist');
    newName = newName.replace('Aukt. Hudterapeut', '<br />Hudterapeut');
    newName = newName.replace(
      'Injektionssjuksköterska',
      '<br />Injektionssjuksköterska'
    );
    newName = newName.replace('Hudläkare', '<br />Hudläkare');
    newName = newName.replace('Leg. Sjuksköterska', '<br />Injektionssjuksköterska');
    newName = newName.replace(
      'Injektionsbehandlare',
      '<br />Injektionsbehandlare'
    );
    newName = newName.replace('Anestesisjuksköterska', '<br />Injektionssjuksköterska');
    newName = newName.replace('Tandläkare', '<br />Tandläkare');
    newName = newName.replace('LPG-terapeut', '<br />LPG-terapeut');
    return `<span class="practitioner-name">${
      newName.split('<br />')[0]
    }</span><span class="practitioner-title">${
      newName.split('<br />')[1]
    }</span>`;
  },
  formatProviderNameInList: (state) => (name) => {
    /* Se PROVIDER_NAME_HIDDEN_ACTIVITY_IDS ovan: dölj behandlarnamnet för vissa tjänster. */
    var listNameOverride = providerNameOverrideFor(state);
    if (listNameOverride) {
      return listNameOverride.name + ' ' + listNameOverride.title;
    }
    /* Adjust provider names */
    // Providers can be returned as terapeut_id (production config) or as display name.
    // If it's an id and we have meta rows, map to terapeut_name/terapeut_sign for display.
    let str = name == null ? '' : String(name);
    const rows = state.providers && Array.isArray(state.providers.metaRows) ? state.providers.metaRows : [];
    if (str && rows.length) {
      const byId = rows.find((r) => r && String(r.terapeut_id || '').trim() === String(str).trim());
      if (byId) {
        str = String(byId.terapeut_name || byId.terapeut_sign || str);
      }
    }

    str = str.replace(
      'Leg. Läkare. Specialist i plastikkirurgi ',
      'Plastikkirurg'
    );
    str = str.replace(
      'Leg. Läkare. Specialist i plastikkirurgi',
      'Plastikkirurg'
    );
    str = str.replace(
      'Leg. Läkare Specialist i plastikkirurgi',
      'Plastikkirurg'
    );
    // Carolina Ryberg custom title (see formatProviderName above) — before the generic rule.
    str = str.replace('Carolina Ryberg Aukt. Hudterapeut', 'Carolina Ryberg Certified SkinMap Specialist');
    str = str.replace('Aukt. Hudterapeut', 'Hudterapeut');

    str = str.replace(
      'Sandra Sellström Leg. Sjuksköterska',
      'Sandra Sellström Injektionssjuksköterska'
    );
    str = str.replace(
      'Silvia Galaz Leg. Sjuksköterska',
      'Silvia Galaz Injektionssjuksköterska'
    );
    str = str.replace(
      'Anna Hamberg Operationssjuksköterska',
      'Anna Hamberg Specialistsjuksköterska'
    );
    str = str.replace('Leg. Sjuksköterska', 'Injektionssjuksköterska');
    return str;
  },
  formatLocationNameInList: (state) => (location) => {
    return location.short_name;
  },
  selectedSlotFormated(state) {
    var date = moment(state.selectedSlot.date);
    date = date.format('ddd') + ' ' + date.format('D MMMM');
    var capitalizedDate = date.charAt(0).toUpperCase() + date.slice(1);
    return (
      capitalizedDate +
      ' kl. ' +
      state.selectedSlot.time +
      ', ' +
      state.selectedSlot.terapeut_sign
    );
  },
  formIsValid(state, getters) {
    /* Set a new variable that is the social security number of the patient without underscores. This is a workaround that is necessary because we use a mask on the user's input for the social security number */
    var ssnWithoutUnderscores = state.patient.ssn;
    ssnWithoutUnderscores = ssnWithoutUnderscores.replace('_', '');

    var formIsValid = true;

    /*
		After implementing BankID bookings, we no longer have to get the users first and last name since we get it from BankID.
		if (!state.patient.firstName) {
			return false;
		}
		if (!state.patient.lastName) {
			return false;
		}
		*/

    if (ssnWithoutUnderscores.length != 15) {
      formIsValid = false;
    } else {
      /* If the SSN is correct length, validate the age and the validity of the ssn itself */
      /* Validate the ssn */
      var validatedSsn = validateSsn(state.patient.ssn);
      if (validatedSsn) {
        state.ui.formErrors.ssn = false;
        /* If the SSN is valid, also check that the patient is at least 18 years old */
        var validatedAge = validateAge(state.patient.ssn);
        if (validatedAge) {
          state.ui.formErrors.ssnAge = false;
        } else {
          state.ui.formErrors.ssnAge = true;
          formIsValid = false;
        }
      } else {
        state.ui.formErrors.ssn = true;
        formIsValid = false;
      }
    }

    /* Validate the email */
    if (!getters.isEmailValid(state.patient.email)) {
      state.ui.formErrors.email = true;
      formIsValid = false;
    } else {
      state.ui.formErrors.email = false;
    }

    /* Validate the phone */
    if (!getters.isPhoneNumberValid(state.patient.phone)) {
      state.ui.formErrors.phone = true;
      formIsValid = false;
    } else {
      state.ui.formErrors.phone = false;
    }

    if (!state.patient.email) {
      formIsValid = false;
    }
    if (!state.patient.phone) {
      formIsValid = false;
    }
    if (!state.patient.gdprConsent) {
      formIsValid = false;
    }
    return formIsValid;
  },
  getRealWeekday() {
    if (moment().format('e') == '6') {
      return 'Sunday';
    }
    return false;
  },
  toTitleCase: (state) => (phrase) => {
    return phrase
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },
  /** Adjust the treatment length manually if needed.
   * This is done as a hacky workaround to the fact that
   * the ClinicBuddy API can't give us different treatment time lengths
   * for different providers
   **/
  getTreatmentLength(state) {
    var duration = state.activities.selected.activity_duration;

    /* If there is a custom treatment duration for the treatment, set the duration to that time */
    if (state.customTreatmentDurations) {
      if (
        state.customTreatmentDurations[
          'activity_id_' + state.activities.selected.activity_id
        ]
      ) {
        if (
          state.customTreatmentDurations[
            'activity_id_' + state.activities.selected.activity_id
          ]['provider_id_' + state.selectedSlot.terapeut_id]
        ) {
          duration =
            state.customTreatmentDurations[
              'activity_id_' + state.activities.selected.activity_id
            ]['provider_id_' + state.selectedSlot.terapeut_id];
        }
      }
    }

    return duration;

    /* The old hard coded custom durations are listed below. They are not used anymore, but are listed here for completeness. */

    /* If the provider is Magnus Kjelsberg Leg. Läkare. Specialist i plastikkirurg... */
    if (state.selectedSlot.terapeut_id == '94') {
      if (state.activities.selected.activity_id == '15') {
        duration = '45';
      }
      if (state.activities.selected.activity_id == '47') {
        duration = '45';
      }

      if (state.activities.selected.activity_id == '140') {
        duration = '45';
      }
    }

    /* If the provider is Hamid Natghian Leg. Läkare. Specialist i plastikkirurgi... */
    if (state.selectedSlot.terapeut_id == '74') {
      if (state.activities.selected.activity_id == '112') {
        duration = '15';
      }
    }

    /* If the provider is Marcus Ehrström Leg. Läkare. Specialist i plastikkirurgi... */
    if (state.selectedSlot.terapeut_id == '116') {
      if (state.activities.selected.activity_id == '3') {
        // Konsultation Bröstförstoring
        duration = '30';
      }
      if (state.activities.selected.activity_id == '4') {
        // Konsultation Bröstförstoring + lyft
        duration = '30';
      }
      if (state.activities.selected.activity_id == '194') {
        // Konsultation Mommy Makeover
        duration = '30';
      }
    }

    /* If the provider is Pehr Sommar... */
    if (state.selectedSlot.terapeut_id == '142') {
      if (state.activities.selected.activity_id == '24') {
        //Konsultation Armplastik OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '14') {
        //Konsultation Brazilian Butt Lift OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '3') {
        //Konsultation Bröstförstoring OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '4') {
        //Konsultation Bröstförstoring + lyft OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '5') {
        //Konsultation Bröstlyft OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '6') {
        //Konsultation Bröstreduktion OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '211') {
        //Konsultation Buccal Fatpad OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '10') {
        //Konsultation Bukplastik OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '15') {
        //Konsultation Konsultation Facelift / Ansiktslyft OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '9') {
        //Konsultation Fettsugning OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '180') {
        // Konsultation Fettransplantation Ansikte OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '13') {
        //Konsultation Gynekomasti OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '17') {
        //Konsultation Fettransplantation Kropp OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '140') {
        //Konsultation Halslyft OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '7') {
        //Konsultation Implantatbyte OK
        //duration = '30';
      }
      if (state.activities.selected.activity_id == '214') {
        //Konsultation Indragna Bröstvårtor OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '181') {
        //Konsultation Injektionsbehandling OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '12') {
        //Konsultation Intimkirurgi OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '219') {
        // Konsultation Käk-/Ansiktsimplantat OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '193') {
        //Konsultation Lipödem OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '23') {
        //Konsultation Lårplastik OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '212') {
        //Konsultation Läpplyft OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '194') {
        //Konsultation Mommy Makeover OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '209') {
        // Konsultation Rumpförstoring OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '47') {
        // Konsultation Pannlyft/Ögonbrynslyft OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '215') {
        // Konsultation Ryggplastik OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '213') {
        //Konsultation Vårtgårdsplastik OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '11') {
        //Konsultation Ögonlocksplastik OK
        //duration = '45';
      }
      if (state.activities.selected.activity_id == '16') {
        //Konsultation Öronplastik OK
        //duration = '45';
      }
    }

    /* If the provider is Petra Peterson */
    if (state.selectedSlot.terapeut_id == '153') {
      if (state.activities.selected.activity_id == '15') {
        //Konsultation Konsultation Facelift / Ansiktslyft OK
        duration = '60';
      }
      if (state.activities.selected.activity_id == '180') {
        // Konsultation Fettransplantation Ansikte OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '47') {
        // Konsultation Pannlyft/Ögonbrynslyft OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '140') {
        //Konsultation Halslyft OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '6') {
        //Konsultation Bröstreduktion OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '13') {
        //Konsultation Gynekomasti OK
        duration = '45';
      }
    }

    /* If the provider is Said Saidian... */
    if (state.selectedSlot.terapeut_id == '143') {
      if (state.activities.selected.activity_id == '24') {
        //Konsultation Armplastik OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '14') {
        //Konsultation Brazilian Butt Lift OK
        duration = '60';
      }
      if (state.activities.selected.activity_id == '3') {
        //Konsultation Bröstförstoring OK
        duration = '60';
      }
      if (state.activities.selected.activity_id == '4') {
        //Konsultation Bröstförstoring + lyft OK
        duration = '60';
      }
      if (state.activities.selected.activity_id == '5') {
        //Konsultation Bröstlyft OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '6') {
        //Konsultation Bröstreduktion OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '10') {
        //Konsultation Bukplastik OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '9') {
        //Konsultation Fettsugning OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '13') {
        //Konsultation Gynekomasti OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '17') {
        //Konsultation Fettransplantation Kropp OK
        duration = '60';
      }
      if (state.activities.selected.activity_id == '7') {
        //Konsultation Implantatbyte OK
        duration = '60';
      }
      if (state.activities.selected.activity_id == '214') {
        //Konsultation Indragna Bröstvårtor OK
        duration = '30';
      }
      if (state.activities.selected.activity_id == '181') {
        //Konsultation Injektionsbehandling OK
        duration = '30';
      }
      if (state.activities.selected.activity_id == '12') {
        //Konsultation Intimkirurgi OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '193') {
        //Konsultation Lipödem OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '23') {
        //Konsultation Lårplastik OK
        duration = '45';
      }
      if (state.activities.selected.activity_id == '212') {
        //Konsultation Läpplyft OK
        duration = '30';
      }
      if (state.activities.selected.activity_id == '194') {
        //Konsultation Mommy Makeover OK
        duration = '60';
      }
      if (state.activities.selected.activity_id == '213') {
        //Konsultation Vårtgårdsplastik OK
        duration = '30';
      }
      if (state.activities.selected.activity_id == '11') {
        //Konsultation Ögonlocksplastik OK
        duration = '30';
      }
      if (state.activities.selected.activity_id == '16') {
        //Konsultation Öronplastik OK
        duration = '30';
      }
    }

    /* If the provider is Sandra Sellström... */
    if (state.selectedSlot.terapeut_id == '24') {
      if (state.activities.selected.activity_id == '195') {
        //Läppförstoring
        duration = '60';
      }
      if (state.activities.selected.activity_id == '113') {
        //Fillers
        duration = '60';
      }
    }

    /* If the provider is Silvia Galaz... */
    if (state.selectedSlot.terapeut_id == '23') {
      if (state.activities.selected.activity_id == '195') {
        //Läppförstoring
        duration = '60';
      }
      if (state.activities.selected.activity_id == '113') {
        //Fillers
        duration = '60';
      }
    }

    /* If the provider is Denise Fa... */
    if (state.selectedSlot.terapeut_id == '9') {
      if (state.activities.selected.activity_id == '112') {
        //Botox 112
        duration = '15';
      }
      if (state.activities.selected.activity_id == '259') {
        //Glabella(Botox) 259
        duration = '15';
      }
      if (state.activities.selected.activity_id == '262') {
        //Gummysmile(Botox) 262
        duration = '15';
      }
      if (state.activities.selected.activity_id == '261') {
        //Kråksparkar(Botox) 261
        duration = '15';
      }
      if (state.activities.selected.activity_id == '260') {
        //Panna(Botox) 260
        duration = '15';
      }
      if (state.activities.selected.activity_id == '195') {
        //Läppförstoring
        duration = '15';
      }
    }

    /* If the provider is Danielle Fa... (there are two Danielle Fa in Clinicbuddy, one with ID 72 and one with ID 8) */
    if (state.selectedSlot.terapeut_id == '8') {
      if (state.activities.selected.activity_id == '112') {
        //Botox 112
        duration = '15';
      }
      if (state.activities.selected.activity_id == '259') {
        //Glabella(Botox) 259
        duration = '15';
      }
      if (state.activities.selected.activity_id == '262') {
        //Gummysmile(Botox) 262
        duration = '15';
      }
      if (state.activities.selected.activity_id == '261') {
        //Kråksparkar(Botox) 261
        duration = '15';
      }
      if (state.activities.selected.activity_id == '260') {
        //Panna(Botox) 260
        duration = '15';
      }
    }
    /* If the provider is Danielle Fa... (there are two Danielle Fa in Clinicbuddy, one with ID 72 and one with ID 8) */
    if (state.selectedSlot.terapeut_id == '72') {
      if (state.activities.selected.activity_id == '112') {
        //Botox 112
        duration = '15';
      }
      if (state.activities.selected.activity_id == '259') {
        //Glabella(Botox) 259
        duration = '15';
      }
      if (state.activities.selected.activity_id == '262') {
        //Gummysmile(Botox) 262
        duration = '15';
      }
      if (state.activities.selected.activity_id == '261') {
        //Kråksparkar(Botox) 261
        duration = '15';
      }
      if (state.activities.selected.activity_id == '260') {
        //Panna(Botox) 260
        duration = '15';
      }
    }

    /* If the provider is Mattias Elmér */
    if (state.selectedSlot.terapeut_id == '5') {
      /* Konsultation Brazilian Butt Lift */
      if (state.activities.selected.activity_id == '14') {
        //duration = '45';
      }
      /* Konsultation Bröstförstoring */
      if (state.activities.selected.activity_id == '3') {
        //duration = '45';
      }
      /* Konsultation Bröstförstoring + lyft */
      if (state.activities.selected.activity_id == '4') {
        //duration = '45';
      }
      /* Konsultation Bröstlyft */
      if (state.activities.selected.activity_id == '5') {
        //duration = '45';
      }
      /* Konsultation Bröstreduktion */
      if (state.activities.selected.activity_id == '6') {
        //duration = '45';
      }
      /* Konsultation Bukplastik */
      if (state.activities.selected.activity_id == '10') {
        //duration = '45';
      }
      /* Konsultation Fettsugning */
      if (state.activities.selected.activity_id == '9') {
        //duration = '45';
      }
      /* Konsultation Fettransplantation Kropp */
      if (state.activities.selected.activity_id == '17') {
        //duration = '45';
      }
      /* Konsultation Lipödem */
      if (state.activities.selected.activity_id == '193') {
        //duration = '45';
      }
      /* Konsultation Mommy Makeover */
      if (state.activities.selected.activity_id == '194') {
        //duration = '45';
      }
      /* Konsultation Rumpimplantat */
      if (state.activities.selected.activity_id == '209') {
        //duration = '45';
      }
      /* Konsultation Ryggplastik */
      if (state.activities.selected.activity_id == '215') {
        //duration = '45';
      }
    }
  },
  getSlotNiceDate: (state) => (date) => {
    var niceDate = moment(date);
    var day = '<span class="capitalize">' + niceDate.format('ddd') + '</span>';
    niceDate = day + ' ' + niceDate.format('D MMMM');
    return niceDate;
  },

  loading(state) {
    return state.ui.loading;
  },

  noAvailableTimesForWeek(state) {
    if (state.ui.loading.times) {
      return false;
    }

    // Use activeTimes (already filtered by provider + location), so the result matches what the user sees.
    let count = 0;

    if (state.ui && state.ui.calendarViewType === 'month') {
      const dates = state.dates && state.dates.active && Array.isArray(state.dates.active.monthDates)
        ? state.dates.active.monthDates
        : [];
      for (const d of dates) {
        const times = d && Array.isArray(d.activeTimes) ? d.activeTimes : [];
        count += times.length;
      }
      } else {
      const weekdays = state.dates && state.dates.active && Array.isArray(state.dates.active.weekdays)
        ? state.dates.active.weekdays
        : [];
      for (const weekday of weekdays) {
        const times = weekday && Array.isArray(weekday.activeTimes) ? weekday.activeTimes : [];
        count += times.length;
      }
    }

    return count === 0;
  },
  totalAvailableTimes(state) {
    let availableTimes = [];
    for (let i in state.dates.active.weekdays) {
      let weekday = state.dates.active.weekdays[i];
      for (let x in weekday.activeTimes) {
        availableTimes.push(weekday.activeTimes[x]);
      }
    }
    return availableTimes;
  },

  /**
   * Providers aligned with the current selection:
   * - If a location is selected: only providers with times for that location
   * - Else if a city is selected: only providers with times for any location under that city
   * - Else: fall back to the unfiltered provider list
   *
   * Note: while times are loading, we return the unfiltered list to avoid UI flicker.
   */
  providersAvailableForSelection(state) {
    const allProviders = Array.isArray(state.providers.available)
      ? state.providers.available
      : [];
    if (!allProviders.length) return [];

    // Avoid flicker while times are being replaced.
    if (state.ui && state.ui.loading && state.ui.loading.times) {
      return allProviders;
    }

    const times = Array.isArray(state.availableTimes) ? state.availableTimes : [];
    if (!times.length) {
      // If we have no times in the currently loaded window, we cannot reliably filter.
      // Keep the provider picker enabled (fallback to the unfiltered list).
      return allProviders;
    }

    // Which locations are in-scope?
    let allowedLocationIds = null;
    if (state.locations && state.locations.selected && state.locations.selected.id) {
      allowedLocationIds = new Set([String(state.locations.selected.id)]);
    } else if (
      state.cities &&
      state.cities.selected &&
      Array.isArray(state.cities.selected.locations)
    ) {
      allowedLocationIds = new Set(
        state.cities.selected.locations.map((l) => String(l.id)).filter(Boolean)
      );
    }

    const norm = (s) =>
      String(s || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    const providerSet = new Set();
    for (const t of times) {
      if (!t || !t.terapeut_sign) continue;
      if (allowedLocationIds && !allowedLocationIds.has(String(t.location_id))) continue;
      providerSet.add(norm(t.terapeut_sign));
      if (t.terapeut_id != null) {
        const id = String(t.terapeut_id).trim();
        if (id) providerSet.add(id);
      }
    }

    const rows = state.providers && Array.isArray(state.providers.metaRows) ? state.providers.metaRows : [];
    return allProviders.filter((p) => {
      const pStr = p == null ? '' : String(p).trim();
      if (!pStr) return false;
      if (providerSet.has(pStr)) return true;
      if (providerSet.has(norm(pStr))) return true;
      // If provider list uses ids, map id -> sign/name and match that too.
      if (rows.length) {
        const r = rows.find((x) => x && String(x.terapeut_id || '').trim() === pStr);
        if (r) {
          const sign = r.terapeut_sign != null ? String(r.terapeut_sign).trim() : '';
          const name = r.terapeut_name != null ? String(r.terapeut_name).trim() : '';
          if (sign && providerSet.has(norm(sign))) return true;
          if (name && providerSet.has(norm(name))) return true;
          if (providerSet.has(pStr)) return true;
        }
      }
      return false;
    });
  },

  /**
   * Map currently selected provider (string name) to ClinicBuddy terapeut_id if we have metadata.
   * Returns a string id or null.
   */
  selectedProviderId(state) {
    const selected = state.providers && state.providers.selected ? String(state.providers.selected).trim() : '';
    if (!selected) return null;
    const rows = state.providers && Array.isArray(state.providers.metaRows) ? state.providers.metaRows : [];
    if (!rows.length) return null;

    // If selection already IS a terapeut_id, return it directly.
    for (const r of rows) {
      if (!r) continue;
      const id = r.terapeut_id != null ? String(r.terapeut_id).trim() : '';
      if (id && id === selected) {
        return id;
      }
    }
    const norm = (s) =>
      String(s || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    const selectedNorm = norm(selected);
    for (const r of rows) {
      if (!r) continue;
      const sign = r.terapeut_sign != null ? String(r.terapeut_sign).trim() : '';
      // Some ClinicBuddy installations use terapeut_name in the provider list; support both.
      const name = r.terapeut_name != null ? String(r.terapeut_name).trim() : '';
      if (
        (sign && sign === selected) ||
        (name && name === selected) ||
        // Fuzzy fallback: configured provider strings from WP admin may include titles/suffixes.
        // Match when either side contains the other (case/whitespace-insensitive).
        (sign && selectedNorm && (norm(sign).includes(selectedNorm) || selectedNorm.includes(norm(sign)))) ||
        (name && selectedNorm && (norm(name).includes(selectedNorm) || selectedNorm.includes(norm(name))))
      ) {
        const id = r.terapeut_id != null ? String(r.terapeut_id) : '';
        return id || null;
      }
    }
    return null;
  },

  showNextAvailableTimeButton(state) {
    return state.ui.showGetNextAvailableTimeButton;
  },

  getBankIDLocation(state) {
    var location = state.bankIDLocation;
    if (location != 'sameDevice' && location != 'otherDevice') {
      return 'sameDevice';
    }
    return state.bankIDLocation;
  },
  isSigningBankIDOnOtherDevice(state) {
    return state.ui.signingBankIDOnOtherDevice;
  },
  isSigningBankIDOnSameDevice(state) {
    return state.ui.signingBankIDOnSameDevice;
  },
  /* Detect if the user is using a mobile device. From https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser */
  isMobile() {
    let check = false;
    (function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
  },
  isIosOrAndroid() {
    var userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) {
      return true;
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return true;
    }
    return false;
  },
  /* 
	Detect if the user is using iOS 
	https://stackoverflow.com/questions/9038625/detect-if-device-is-ios
	*/
  isIos() {
    return (
      [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
      ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  },
  isChromeOnIos() {
    return navigator.userAgent.match('CriOS');
  },
  isSafariOnIos() {
    var ua = window.navigator.userAgent;
    var iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
    var webkit = !!ua.match(/WebKit/i);
    var iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
    return iOSSafari;
  },
  isAndroid() {
    var userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) {
      return true;
    }
    return false;
  },
  getAutostartUrl(state, getters) {
    var autostartUrl = false;
    /* Generate an autostart URL based on the client, i.e. different autostart URLs for different devices and browsers */
    if (state.booking.autostartToken) {
      /* Set up the URL correctly depending on if we're on IOS or Android, or not */
      /* See https://www.bankid.com/utvecklare/guider/teknisk-integrationsguide/programstart */
      var redirectUrl = 'null';
      if (getters.isIosOrAndroid) {
        /* If the user is using iOS and is using Safari, we can redirect them to the ordinary page URL since it'll open Safari for the user after signing with BankID. We add a hash to the URL to force the Safari browser to open the same tab in the browser. */
        if (getters.isIos) {
          /* If the user is using iOS and is using Chrome, we can redirect them to a specific Chrome URL to force iOS to open Chrome when the BankID signing is complete. */
          if (getters.isChromeOnIos) {
            /* For Chrome on iOS, just set the redirect URL to googlechrome://. This will open up the Chrome browser in the latest tab when the user has signed, i.e. the tab containing the booking page. */
            redirectUrl = 'googlechrome://';
          } else {
            /* If we're not on Chrome, we're probably on Safari, so contruct a redirect url accordingly. */
            // BankID expects the redirect param to be URL-encoded. Using encodeURI leaves characters
            // like ? & # unescaped, which can break parsing (especially if the booking page has query params).
            const baseUrl = window.location.href.split('#')[0];
            redirectUrl = encodeURIComponent(baseUrl + '#authComplete');
          }
        }
        if (getters.isAndroid) {
          /* If the user is using Android, we set the redirect parameter to "null" since this will ensure that the user will return to their previously used browser after signing with BankID, according to the BankID integration guide. */
          redirectUrl = 'null';
        }
      }
      autostartUrl =
        'bankid:///?autostarttoken=' +
        state.booking.autostartToken +
        '&redirect=' +
        redirectUrl;
    }
    return autostartUrl;
  },

  isEmailValid: (state) => (email) => {
    var isValid = false;
    var re = /\S+@\S+\.\S+/;
    isValid = re.test(email);
    return isValid;
  },

  isPhoneNumberValid: (state) => (phone) => {
    var isValid = false;
    var minLength = 7;
    var maxLength = 17;
    if (phone.length >= minLength && phone.length <= maxLength) {
      isValid = true;
    }

    return isValid;
  },

  isActivityBookable: (state) => (activityId) => {
    var isBookable = true;
    if (state.unbookableTreatments) {
      if (state.unbookableTreatments.includes(parseInt(activityId))) {
        isBookable = false;
      }
    }
    return isBookable;
  },

  // City-related getters
  filteredActivityGroups: (state, getters) => {
    // While we're actively loading city-specific group ids and we don't have a result yet,
    // do NOT blank the UI (it looks broken). Keep showing the existing list while the spinner runs.
    if (
      state.cities &&
      state.cities.selected &&
      state.ui &&
      state.ui.loading &&
      state.ui.loading.activityGroupsForCity &&
      (state.activityGroups.cityBookableGroupIds === null ||
        typeof state.activityGroups.cityBookableGroupIds === 'undefined')
    ) {
      return state.activityGroups.bookable;
    }

    if (
      state.cities &&
      state.cities.selected &&
      Array.isArray(state.activityGroups.cityBookableGroupIds)
    ) {
      const allowed = new Set(
        state.activityGroups.cityBookableGroupIds.map((x) => String(x))
      );
      return (state.activityGroups.bookable || []).filter((g) =>
        allowed.has(String(g.activity_group_id))
      );
    }
    return state.activityGroups.bookable;
  },

  filteredActivities: (state, getters) => {
    let list;
    if (
      state.cities &&
      state.cities.selected &&
      Array.isArray(state.activities.cityBookableIds) &&
      state.activities.cityBookableIds.length > 0
    ) {
      const allowed = new Set(state.activities.cityBookableIds.map((x) => String(x)));
      list = (state.activities.active || []).filter(
        (a) => a && a.activity_id && allowed.has(String(a.activity_id))
      );
    } else {
      list = state.activities.active || [];
    }
    // Return a sorted copy (never sort state in place inside a getter).
    return list.slice().sort(byActivityName);
  },

  isActivityAvailableInCity:
    (state, getters) =>
    (activity, cityId = null) => {
      const targetCityId =
        cityId || (state.cities.selected ? state.cities.selected.id : null);

      if (!targetCityId) {
        return true; // Show all activities if no city is selected
      }

      // Find the matching price data for this activity
      const priceData = getters.findPriceDataForActivity(activity.activity_id);

      if (priceData && priceData.cities && priceData.cities.length > 0) {
        // Use price data cities if available
        return priceData.cities.some((city) => city.id == targetCityId);
      }

      // Fallback: if we don't have city metadata for this activity, don't hide it.
      // Times will still be filtered by the city's allowed location_ids.
        return true;
    },

  // Add helper getter
  findPriceDataForActivity: (state) => (activityId) => {
    if (!state.priceData.available) {
      return null;
    }

    // Search through all price data to find matching activity_id
    for (let areaKey in state.priceData.available) {
      const area = state.priceData.available[areaKey];
      if (area.treatments) {
        for (let treatmentKey in area.treatments) {
          const treatment = area.treatments[treatmentKey];
          if (treatment.treatments) {
            for (let singleTreatment of treatment.treatments) {
              if (singleTreatment.activity_id == activityId) {
                return singleTreatment;
              }
            }
          }
        }
      }
    }
    return null;
  },

  isInAppBrowser() {
    return /Instagram|FBAN|FBAV|FB_IAB|FBAN|FBAV|FBIOS|FB_IAB|Twitter|LinkedInApp|Line|WhatsApp|Telegram|Snapchat|TikTok|Pinterest|WeChat|QQ/i.test(
      navigator.userAgent
    );
  },
};
